import React, { useState, useMemo, useRef } from 'react';
import { SECTIONS, UNIT_OPTIONS, DEFAULT_COST_UNIT, UNIT_TO_FT } from '../data/ingredients';
import styles from './IngredientDatabase.module.css';

// ── Main Component ────────────────────────────────────────────
export default function IngredientDatabase({ ingDB, onClose }) {
  const [searchQuery,   setSearchQuery]   = useState('');
  const [activeSection, setActiveSection] = useState('all');
  const [editingId,     setEditingId]     = useState(null);
  const [addModal,      setAddModal]      = useState(null); // null | sectionKey
  const searchRef = useRef(null);

  const { db, allIngredients, searchIngredients, updateIngredientFull,
          addIngredient, removeIngredient, resetToDefaults } = ingDB;

  // ── Filtered list ─────────────────────────────────────────
  const displayRows = useMemo(() => {
    if (searchQuery.trim()) return searchIngredients(searchQuery);
    if (activeSection === 'all') return allIngredients;
    return (db[activeSection] || []).map(ing => ({
      ...ing,
      sectionKey: activeSection,
      sectionLabel: SECTIONS.find(s => s.key === activeSection)?.label || activeSection,
      sectionColor: SECTIONS.find(s => s.key === activeSection)?.color || 'var(--accent)',
    }));
  }, [searchQuery, activeSection, allIngredients, db, searchIngredients]);

  const handleReset = () => {
    if (window.confirm('Reset all ingredients to factory defaults? Custom ingredients and all cost prices will be lost.')) {
      resetToDefaults();
      setEditingId(null);
    }
  };

  return (
    <div className={styles.overlay}>

      {/* ── HEADER ── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}>🗂️</span>
          <div>
            <div className={styles.headerTitle}>Ingredient Database</div>
            <div className={styles.headerSub}>
              {allIngredients.length} ingredients across 12 sections
            </div>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnReset} onClick={handleReset}
            title="Reset all ingredients to defaults">
            Reset to defaults
          </button>
          <button className={styles.btnClose} onClick={onClose}>✕ Close</button>
        </div>
      </div>

      {/* ── SEARCH BAR ── */}
      <div className={styles.searchBar}>
        <div className={styles.searchWrap}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            ref={searchRef}
            className={styles.searchInput}
            type="text"
            placeholder="Search ingredients by name or note…"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setActiveSection('all'); }}
          />
          {searchQuery && (
            <button className={styles.searchClear}
              onClick={() => { setSearchQuery(''); searchRef.current?.focus(); }}>
              ✕
            </button>
          )}
        </div>
        <div className={styles.searchCount}>
          {displayRows.length} result{displayRows.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className={styles.body}>

        {/* ── LEFT NAV ── */}
        <nav className={styles.nav}>
          <button
            className={`${styles.navItem} ${activeSection === 'all' ? styles.navActive : ''}`}
            onClick={() => { setActiveSection('all'); setSearchQuery(''); }}
          >
            <span className={styles.navIcon}>📋</span>
            <span className={styles.navLabel}>All Ingredients</span>
            <span className={styles.navCount}>{allIngredients.length}</span>
          </button>

          {SECTIONS.map(sect => (
            <button
              key={sect.key}
              className={`${styles.navItem} ${activeSection === sect.key ? styles.navActive : ''}`}
              onClick={() => { setActiveSection(sect.key); setSearchQuery(''); }}
              style={{ '--sect-color': sect.color }}
            >
              <span className={styles.navDot} />
              <span className={styles.navLabel}>{sect.label}</span>
              <span className={styles.navCount}>{(db[sect.key] || []).length}</span>
            </button>
          ))}
        </nav>

        {/* ── TABLE ── */}
        <div className={styles.tableWrap}>

          {/* Section header + Add button */}
          <div className={styles.tableHeader}>
            <div className={styles.tableTitle}>
              {searchQuery
                ? `Search results for "${searchQuery}"`
                : activeSection === 'all'
                  ? 'All Ingredients'
                  : SECTIONS.find(s => s.key === activeSection)?.label}
            </div>
            {activeSection !== 'all' && !searchQuery && (
              <button
                className={styles.btnAdd}
                onClick={() => setAddModal(activeSection)}
              >
                + Add ingredient
              </button>
            )}
          </div>

          {/* Column headers */}
          <div className={styles.colHeaders}>
            <span className={styles.colSection}>Section</span>
            <span className={styles.colName}>Ingredient</span>
            <span className={styles.colRange}>Range / Ft·lb</span>
            <span className={styles.colMax}>Max %</span>
            <span className={styles.colCost}>Cost</span>
            <span className={styles.colUnit}>Unit</span>
            <span className={styles.colNote}>Notes</span>
            <span className={styles.colActions} />
          </div>

          {/* Rows */}
          <div className={styles.rows}>
            {displayRows.length === 0 ? (
              <div className={styles.empty}>
                {searchQuery ? `No ingredients match "${searchQuery}"` : 'No ingredients in this section.'}
              </div>
            ) : displayRows.map((ing, idx) => (
              editingId === ing.id
                ? <EditRow
                    key={ing.id}
                    ing={ing}
                    onSave={(updated) => {
                      updateIngredientFull(ing.sectionKey, ing.id, updated);
                      setEditingId(null);
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                : <ReadRow
                    key={ing.id}
                    ing={ing}
                    isAlt={idx % 2 === 1}
                    showSection={activeSection === 'all' || searchQuery}
                    onEdit={() => setEditingId(ing.id)}
                    onDelete={() => {
                      if (window.confirm(`Remove "${ing.name}" from the database?`)) {
                        removeIngredient(ing.sectionKey, ing.id);
                      }
                    }}
                  />
            ))}
          </div>

          {/* Add button at bottom of section */}
          {activeSection !== 'all' && !searchQuery && (
            <button className={styles.addRowBtn}
              onClick={() => setAddModal(activeSection)}>
              + Add ingredient to {SECTIONS.find(s => s.key === activeSection)?.label}
            </button>
          )}
        </div>
      </div>

      {/* ── ADD INGREDIENT MODAL ── */}
      {addModal && (
        <AddIngredientModal
          sectionKey={addModal}
          sectionLabel={SECTIONS.find(s => s.key === addModal)?.label || addModal}
          onAdd={(data) => {
            addIngredient(addModal, data);
            setAddModal(null);
          }}
          onClose={() => setAddModal(null)}
        />
      )}
    </div>
  );
}

// ── Read-only row ─────────────────────────────────────────────
function ReadRow({ ing, isAlt, showSection, onEdit, onDelete }) {
  const sect = SECTIONS.find(s => s.key === ing.sectionKey);
  const unitOpt = UNIT_OPTIONS.find(u => u.value === ing.costUnit);

  return (
    <div className={`${styles.row} ${isAlt ? styles.rowAlt : ''}`}>
      {showSection && (
        <span className={styles.cellSection}
          style={{ color: ing.sectionColor || 'var(--accent)' }}>
          <span className={styles.sectionDot}
            style={{ background: ing.sectionColor || 'var(--accent)' }} />
          {ing.sectionLabel}
        </span>
      )}
      {!showSection && <span className={styles.cellSection} />}

      <span className={styles.cellName}>
        {ing.name}
        {ing.isCustom && <span className={styles.customBadge}>custom</span>}
      </span>

      <span className={styles.cellRange}>
        {ing.isCasing
          ? `${ing.typMin} ft/lb`
          : `${ing.typMin}–${ing.typMax}%`}
      </span>

      <span className={styles.cellMax}>
        {ing.isCasing ? '—' : `${ing.hardMax}%`}
      </span>

      <span className={styles.cellCost}>
        {ing.costPerUnit > 0
          ? `$${parseFloat(ing.costPerUnit).toFixed(2)}`
          : <span className={styles.noPrice}>—</span>}
      </span>

      <span className={styles.cellUnit}>
        {unitOpt ? unitOpt.label.replace('$ / ', '') : ing.costUnit}
      </span>

      <span className={styles.cellNote} title={ing.note}>{ing.note}</span>

      <span className={styles.cellActions}>
        <button className={styles.editBtn} onClick={onEdit} title="Edit">✎</button>
        <button className={styles.deleteBtn} onClick={onDelete} title="Remove">✕</button>
      </span>
    </div>
  );
}

// ── Editable row ──────────────────────────────────────────────
function EditRow({ ing, onSave, onCancel }) {
  const [data, setData] = useState({
    ...ing,
    macroProtein:   ing.macro ? ing.macro[0] : '',
    macroCarbs:     ing.macro ? ing.macro[1] : '',
    macroFat:       ing.macro ? ing.macro[2] : '',
    macroSodium:    ing.macro ? ing.macro[3] : '',
    macroCalories:  ing.macro ? ing.macro[4] : '',
  });
  const [showMacro, setShowMacro] = useState(false);
  const set = (field, val) => setData(d => ({ ...d, [field]: val }));

  return (
    <div className={`${styles.row} ${styles.rowEditing}`}>
      <span className={styles.cellSection}>
        <span className={styles.editingLabel}>Editing</span>
      </span>

      <span className={styles.cellName}>
        <input className={styles.editInput} value={data.name}
          onChange={e => set('name', e.target.value)} placeholder="Ingredient name" />
      </span>

      <span className={styles.cellRange}>
        <div className={styles.rangeInputs}>
          <input className={styles.editInputSm} type="text" inputMode="decimal"
            value={data.typMin} onChange={e => set('typMin', e.target.value)}
            placeholder="min" />
          <span className={styles.rangeSep}>–</span>
          <input className={styles.editInputSm} type="text" inputMode="decimal"
            value={data.typMax} onChange={e => set('typMax', e.target.value)}
            placeholder="max" />
        </div>
      </span>

      <span className={styles.cellMax}>
        <input className={styles.editInputSm} type="text" inputMode="decimal"
          value={data.hardMax} onChange={e => set('hardMax', e.target.value)}
          placeholder="max" />
      </span>

      <span className={styles.cellCost}>
        <input className={styles.editInputSm} type="text" inputMode="decimal"
          value={data.costPerUnit || ''} onChange={e => set('costPerUnit', e.target.value)}
          placeholder="0.00" />
      </span>

      <span className={styles.cellUnit}>
        <select className={styles.editSelect} value={data.costUnit}
          onChange={e => set('costUnit', e.target.value)}>
          {UNIT_OPTIONS.map(u => (
            <option key={u.value} value={u.value}>{u.label}</option>
          ))}
        </select>
      </span>

      <span className={styles.cellNote}>
        <input className={styles.editInput} value={data.note || ''}
          onChange={e => set('note', e.target.value)} placeholder="Notes…" />
      </span>

      <span className={styles.cellMacroEdit}>
        <button type="button" className={styles.macroToggleInline}
          onClick={() => setShowMacro(s => !s)}>
          {showMacro ? '▾ hide nutrition' : '▸ nutrition/100g'}
        </button>
        {showMacro && (
          <div className={styles.macroInlineGrid}>
            {[
              { label: 'Protein', key: 'macroProtein' },
              { label: 'Carbs',   key: 'macroCarbs'   },
              { label: 'Fat',     key: 'macroFat'     },
              { label: 'Sodium',  key: 'macroSodium'  },
              { label: 'Cal',     key: 'macroCalories'},
            ].map(f => (
              <label key={f.key} className={styles.macroInlineLabel}>
                {f.label}
                <input className={styles.editInputSm}
                  type="text" inputMode="decimal" placeholder="0"
                  value={data[f.key] || ''}
                  onChange={e => set(f.key, e.target.value)} />
              </label>
            ))}
          </div>
        )}
      </span>

      <span className={styles.cellActions}>
        <button className={styles.saveBtn}
          onClick={() => {
          const hasMacro = data.macroProtein || data.macroCarbs || data.macroFat || data.macroSodium || data.macroCalories;
          onSave({
            ...data,
            costPerUnit: parseFloat(data.costPerUnit) || 0,
            typMin:      parseFloat(data.typMin)      || 0,
            typMax:      parseFloat(data.typMax)      || 0,
            hardMax:     parseFloat(data.hardMax)     || 0,
            macro: hasMacro ? [
              parseFloat(data.macroProtein)  || 0,
              parseFloat(data.macroCarbs)    || 0,
              parseFloat(data.macroFat)      || 0,
              parseFloat(data.macroSodium)   || 0,
              parseFloat(data.macroCalories) || 0,
            ] : data.macro || null,
          });
        }}>
          ✓
        </button>
        <button className={styles.cancelBtn} onClick={onCancel}>✕</button>
      </span>
    </div>
  );
}

// ── Add Ingredient Modal ──────────────────────────────────────
function AddIngredientModal({ sectionKey, sectionLabel, onAdd, onClose }) {
  const defaultUnit = DEFAULT_COST_UNIT[sectionKey] || 'oz';
  const [data, setData] = useState({
    name: '', typMin: '', typMax: '', hardMax: '',
    note: '', costPerUnit: '', costUnit: defaultUnit,
    macroProtein: '', macroCarbs: '', macroFat: '',
    macroSodium: '', macroCalories: '',
  });
  const [showMacro, setShowMacro] = useState(false);
  const set = (field, val) => setData(d => ({ ...d, [field]: val }));
  const canSave = data.name.trim().length > 0;

  const handleAdd = () => {
    if (!canSave) return;
    const hasMacro = data.macroProtein || data.macroCarbs || data.macroFat ||
                     data.macroSodium  || data.macroCalories;
    const macro = hasMacro ? [
      parseFloat(data.macroProtein)  || 0,
      parseFloat(data.macroCarbs)    || 0,
      parseFloat(data.macroFat)      || 0,
      parseFloat(data.macroSodium)   || 0,
      parseFloat(data.macroCalories) || 0,
    ] : null;
    onAdd({
      ...data,
      typMin:      parseFloat(data.typMin)      || 0,
      typMax:      parseFloat(data.typMax)      || 0,
      hardMax:     parseFloat(data.hardMax)     || 0,
      costPerUnit: parseFloat(data.costPerUnit) || 0,
      macro,
    });
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        <div className={styles.modalHeader}>
          <div>
            <div className={styles.modalTitle}>Add Ingredient</div>
            <div className={styles.modalSub}>Adding to: {sectionLabel}</div>
          </div>
          <button className={styles.btnClose} onClick={onClose}>✕</button>
        </div>

        <div className={styles.modalBody}>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Ingredient Name *</label>
            <input className={styles.formInput} type="text"
              placeholder="e.g. Wild Boar Trim"
              value={data.name} onChange={e => set('name', e.target.value)}
              autoFocus />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Typical % Range</label>
            <div className={styles.formInline}>
              <input className={styles.formInputSm} type="text" inputMode="decimal"
                placeholder="Min" value={data.typMin}
                onChange={e => set('typMin', e.target.value)} />
              <span className={styles.formSep}>to</span>
              <input className={styles.formInputSm} type="text" inputMode="decimal"
                placeholder="Max" value={data.typMax}
                onChange={e => set('typMax', e.target.value)} />
              <span className={styles.formSep}>%</span>
            </div>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Hard Maximum %</label>
            <div className={styles.formInline}>
              <input className={styles.formInputSm} type="text" inputMode="decimal"
                placeholder="e.g. 40" value={data.hardMax}
                onChange={e => set('hardMax', e.target.value)} />
              <span className={styles.formSep}>%</span>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Cost (optional)</label>
            <div className={styles.formInline}>
              <span className={styles.formPrefix}>$</span>
              <input className={styles.formInputSm} type="text" inputMode="decimal"
                placeholder="0.00" value={data.costPerUnit}
                onChange={e => set('costPerUnit', e.target.value)} />
              <span className={styles.formSep}>per</span>
              <select className={styles.formSelect} value={data.costUnit}
                onChange={e => set('costUnit', e.target.value)}>
                {UNIT_OPTIONS.map(u => (
                  <option key={u.value} value={u.value}>
                    {u.label.replace('$ / ', '')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Optional nutrition section ── */}
          <div className={styles.formRow}>
            <button
              type="button"
              className={styles.macroToggle}
              onClick={() => setShowMacro(s => !s)}
            >
              {showMacro ? '▾' : '▸'} Nutrition per 100g
              <span className={styles.macroToggleSub}>(optional — for Nutrition Estimator)</span>
            </button>
          </div>

          {showMacro && (
            <div className={styles.macroGrid}>
              {[
                { label: 'Protein (g)',   key: 'macroProtein'  },
                { label: 'Carbs (g)',     key: 'macroCarbs'    },
                { label: 'Fat (g)',       key: 'macroFat'      },
                { label: 'Sodium (mg)',   key: 'macroSodium'   },
                { label: 'Calories (kcal)', key: 'macroCalories' },
              ].map(field => (
                <div key={field.key} className={styles.macroField}>
                  <label className={styles.formLabel}>{field.label}</label>
                  <input
                    className={styles.formInputSm}
                    type="text" inputMode="decimal"
                    placeholder="0"
                    value={data[field.key]}
                    onChange={e => set(field.key, e.target.value)}
                  />
                </div>
              ))}
              <div className={styles.macroNote}>
                Values per 100g of ingredient — source from USDA FoodData Central (fdc.nal.usda.gov) or product spec sheet.
              </div>
            </div>
          )}

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Notes</label>
            <textarea className={styles.formTextarea}
              placeholder="Usage tips, substitutions, sourcing notes…"
              value={data.note} onChange={e => set('note', e.target.value)}
              rows={3} />
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnCancel} onClick={onClose}>Cancel</button>
          <button className={styles.btnSave} onClick={handleAdd} disabled={!canSave}>
            Add to Database
          </button>
        </div>
      </div>
    </div>
  );
}

// Re-export for use in IngredientGrid "Add new" dropdown
export { AddIngredientModal };
